// // src/api/company.ts
import axios from "axios";
import { CompanyApiResponse } from "../types/companyTypes";
import * as Sentry from "@sentry/react";

export interface CreateCompanyRequest {
  kbo_number: string;
  company_legal_name: string;
  company_vat: string;
  company_founded: string;
  company_nace_code: string;
  company_legal_form: string;
  company_email: string;
  company_phone: string;
  company_website: string;
  company_address_street: string;
  company_address_number: string;
  company_address_zip: string;
  company_address_city: string;
  company_address_country: string;
  user_id: string;
  billing_email: string;
  billing_address_street: string;
  billing_address_number: string;
  billing_address_zip: string;
  billing_address_city: string;
  billing_address_country: string;
  invoicing_cycle: string;
  iban: string;
}

/**
 * Errors thrown by this module hang the HTTP status and parsed body off
 * `response`, the way axios does, so callers can branch on
 * `error.response.status` without knowing whether fetch or axios threw
 * (see `statusOf` in components/management/customerSwitch/CreateCompanyStep.tsx).
 */
export interface CompanyApiError extends Error {
  response: { status: number; data: unknown };
}

export async function searchCompany(cleanedTerm: string, token: string): Promise<CompanyApiResponse> {
  try {
    const res = await fetch(
      `${import.meta.env.VITE_AUTH_BASE_URL}/api/v1/company/search/${encodeURIComponent(cleanedTerm)}`,
      {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      }
    );
    if (!res.ok) {
      const error = new Error("Company not found");
      Sentry.captureException(error);
      throw error;
    }
    const data: CompanyApiResponse = await res.json();
    if (!data?.companies || data.companies.length === 0) {
      const error = new Error("No companies found");
      Sentry.captureException(error);
      throw error;
    }
    return data;
  } catch (error) {
    Sentry.captureException(error);
    throw error;
  }
}

/**
 * One row of `GET /api/v1/company/search/:term` — the Belgian enterprise register
 * (`kbo`), NOT our own `companies` table.
 *
 * 🔴 `existsInCompanies` / `linkedToCaller` are the FE↔BE contract for the Customer
 * Switch destination picker. Both are additive and per-row; the field names are
 * character-exact and must not be renamed on one side only.
 *
 *   existsInCompanies — a `companies` row has `companyNumber = kbo.number`
 *   linkedToCaller    — the authenticated caller is in `company_contact` for it
 *
 * They are typed OPTIONAL on purpose: a frontend deployed ahead of the backend sees
 * them as `undefined`, and every consumer here treats `undefined` as "not safe to
 * offer as new". That degrades the register section to empty, which is exactly
 * today's behaviour, rather than offering a create that would 409.
 */
export interface KboSearchRow {
  companyName: string | null;
  vatNumber: string | null;
  addressLine1: string | null;
  addressLine2?: string | null;
  addressNumber: string | null;
  postalCode: string | null;
  city: string | null;
  country: string | null;
  contact?: {
    email?: string | null;
    web?: string | null;
    telephone?: string | null;
  };
  metadata?: {
    number?: number | null;
    numberText?: string | null;
    formattedNumber?: string | null;
    startDate?: string | null;
    type?: string | null;
    legalForm?: string | null;
    juridicalSituation?: string | null;
    NACECode?: string | null;
  };
  existsInCompanies?: boolean;
  linkedToCaller?: boolean;
}

/**
 * Register search for a TYPEAHEAD. Deliberately NOT `searchCompany`.
 *
 * `searchCompany` throws on a 404 AND on an empty result set, and calls
 * `Sentry.captureException` twice per failure. Under a debounced typeahead "no
 * match yet" is the commonest outcome by a wide margin, so reusing it would send a
 * Sentry event every time the user pauses mid-word. Here:
 *
 *   - 404 or an empty body  → `[]`. Not an error; the register simply has no match.
 *   - 5xx / network / parse → throws, and IS reported. Something is actually wrong.
 *   - other 4xx (e.g. 429)  → throws, NOT reported. The global rate limiter is a
 *                             known, expected condition, not a defect to page on.
 *
 * `searchCompany` is left untouched: `AddCompanyPage` depends on its throwing shape
 * inside `toast.promise`.
 */
export async function searchKboCompanies(
  term: string,
  token: string,
  signal?: AbortSignal
): Promise<KboSearchRow[]> {
  const res = await fetch(
    `${import.meta.env.VITE_AUTH_BASE_URL}/api/v1/company/search/${encodeURIComponent(term)}`,
    {
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      signal,
    }
  );

  if (res.status === 404) return [];

  if (!res.ok) {
    const error = new Error(`Company register search failed (${res.status})`) as Error & {
      response?: { status: number };
    };
    error.response = { status: res.status };
    if (res.status >= 500) Sentry.captureException(error);
    throw error;
  }

  const data = (await res.json().catch(() => null)) as { companies?: KboSearchRow[] } | null;
  return Array.isArray(data?.companies) ? data.companies : [];
}

/**
 * The distinguishable 409 body `POST /api/v1/company` returns when the company is
 * already in `companies` — from the pre-check AND from the `ER_DUP_ENTRY` race.
 *
 * `linkedToCaller` is what makes the race recoverable deterministically: `true`
 * means the caller is already a contact, so the destination can simply be selected
 * and the switch continues; `false` means somebody else owns it and the flow has to
 * go back to the list.
 */
export interface CreateCompanyConflict {
  error?: string;
  code?: string;
  company_id?: number | string;
  linkedToCaller?: boolean;
}

export async function createCompany(payload: CreateCompanyRequest, token: string): Promise<CompanyApiResponse> {
  try {
    const response = await fetch(`${import.meta.env.VITE_AUTH_BASE_URL}/api/v1/company`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      // Create a custom error object
      const error = new Error() as CompanyApiError;
      error.response = {
        status: response.status,
        data: await response.json().catch(() => ({})),
      };

      // Set specific error messages based on status code
      switch (response.status) {
        case 409:
          error.message = "Company already exists";
          break;
        case 401:
          error.message = "Unauthorized access";
          break;
        case 500:
          error.message = "Internal server error";
          break;
        default:
          error.message = "Failed to add the company";
      }

      Sentry.captureException(error);
      throw error;
    }

    return response.json();
  } catch (error) {
    Sentry.captureException(error);
    throw error;
  }
}

interface AccessRequestPayload {
  companyName: string;
  companyVat: string;
  message: string;
  kbo_number: string;
}

export async function sendAccessRequest(payload: AccessRequestPayload, token: string): Promise<void> {
  const response = await fetch(`${import.meta.env.VITE_AUTH_BASE_URL}/api/v1/company/mail`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    // Create an error and add a custom "response"
    const error = new Error("Failed to send access request") as CompanyApiError;
    // Attach the status and any parsed response data
    error.response = {
      status: response.status,
      data: await response.json().catch(() => ({})), // parse body or return an empty object
    };
    throw error;
  }
}

export const checkCompanyExists = async (kbo_number: string, token: string) => {
  try {
    const response = await axios.get(
      `${import.meta.env.VITE_AUTH_BASE_URL}/api/v1/company/check/${encodeURIComponent(kbo_number)}`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );

    console.log("checkCompanyExists response:", response.data);

    // Simply return the response data since the API already returns
    // the correct format: { exists: boolean, company: {...} }
    return response.data;
  } catch (error) {
    console.error("Error in checkCompanyExists:", error);
    throw error;
  }
};
